import { Fragment } from 'react';
import c from 'classnames';

import { Icon, Typography } from '@repo/ui';

import { ActivationStep, ActivationStepKey } from 'hooks/useActivationSteps';

import styles from './ActivationStepper.module.css';

export type ActivationStepperProps = {
  steps: ActivationStep[];
  currentStepKey: ActivationStepKey;
  allCompleted?: boolean;
};

export const ActivationStepper = ({
  steps,
  currentStepKey,
  allCompleted = false,
}: ActivationStepperProps) => {
  const currentIndex = steps.findIndex(step => step.key === currentStepKey);

  const gridTemplateColumns = steps
    .map((_, index) => (index === 0 ? 'auto' : '1fr auto'))
    .join(' ');

  const isSegmentFilled = (rightIndex: number) =>
    allCompleted || (currentIndex >= 0 && rightIndex <= currentIndex);

  return (
    <div
      className={styles.stepper}
      style={{ gridTemplateColumns }}
      data-testid='activation-stepper'
    >
      {steps.map((step, index) => {
        const isCompleted =
          allCompleted || (currentIndex >= 0 && index < currentIndex);
        const isCurrent = !allCompleted && index === currentIndex;
        const state = isCompleted
          ? 'completed'
          : isCurrent
            ? 'current'
            : 'pending';

        const lineLeft =
          index === 0 ? 'none' : isSegmentFilled(index) ? 'filled' : 'empty';
        const lineRight =
          index === steps.length - 1
            ? 'none'
            : isSegmentFilled(index + 1)
              ? 'filled'
              : 'empty';

        return (
          <Fragment key={step.key}>
            {index > 0 && (
              <div
                className={c(
                  styles.connector,
                  isSegmentFilled(index) && styles.connectorFilled,
                )}
              />
            )}
            <div
              className={styles.stepCell}
              data-line-left={lineLeft}
              data-line-right={lineRight}
            >
              <div
                className={c(
                  styles.stepCircle,
                  isCurrent && styles.filled,
                  isCompleted && styles.done,
                )}
                data-testid='activation-stepper-circle'
                data-state={state}
              >
                {isCompleted ? (
                  <Icon name='done' size='s' className={styles.stepCheck} />
                ) : (
                  <Typography
                    variant='label'
                    className={c(
                      styles.stepNumber,
                      isCurrent && styles.stepNumberFilled,
                    )}
                  >
                    {index + 1}
                  </Typography>
                )}
              </div>
              <Typography
                variant='label'
                className={c(
                  styles.stepLabel,
                  isCurrent && styles.stepLabelFilled,
                )}
              >
                {step.label}
              </Typography>
            </div>
          </Fragment>
        );
      })}
    </div>
  );
};
